import {
  FC,
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { Region } from '../../../utils';
import { useAuthFactory } from '../AuthFactoryContext';
import { UseAuthContextType } from '../auth0/auth.types';
import { getDefaultUseAuthState } from '../auth0/helpers';
import { getRegionFromTokenOrDefault } from '../auth0/jwt';

type OverrideAuthContextType = UseAuthContextType;

const OverrideAuthDefaultState = getDefaultUseAuthState({
  user: { name: 'override', picture: undefined, email: 'email@email.com' },
  isAuthenticated: true,
});

const OverrideAuthContext = createContext<OverrideAuthContextType>(
  OverrideAuthDefaultState,
);

export const AuthenticationProvider: FC<{
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
}> = ({ onTokenReceived, children }) => {
  const { overrideToken, setOverrideToken } = useAuthFactory();

  const value = useMemo<OverrideAuthContextType>(
    () => ({
      ...OverrideAuthDefaultState,
      token: overrideToken,
      logout: async () => setOverrideToken(undefined),
    }),

    [OverrideAuthDefaultState, overrideToken, setOverrideToken],
  );

  useEffect(() => {
    if (value.token) {
      onTokenReceived(value.token, getRegionFromTokenOrDefault(value.token));
    }
  }, [value]);

  return (
    <OverrideAuthContext.Provider value={value}>
      {children}
    </OverrideAuthContext.Provider>
  );
};

export const useAuth = () => useContext(OverrideAuthContext);
